// PANEL B — THE BALLOTS. Every ballot on this sitting that setup can still act on,
// each shown with only the acts its state permits: a draft may be marked ready or
// abandoned; a ready ballot may be opened (once the roll is frozen) or abandoned. The
// hub GUIDES the order — Open appears only when the roll is frozen — rather than
// claiming control of it; the server re-checks every act.
//
// ── AN OPEN POLL APPEARS HERE, WITH NO ACT ON IT ───────────────────────────
// `GET /api/setup/:electionId` now lists 'open' rows too, so a running poll is on
// this list. Nothing here acts on one: it cannot be marked ready, opened twice or
// abandoned, and its window is not editable through `/schedule`. The ONE act it does
// have — giving it more time — lives in the hub's "Open now" panel above, where the
// close it is about is on the screen beside the control. So the row states what it is
// and says where the act is, rather than sitting silently with no controls, which
// reads as broken. `acts.extend` is computed here all the same (it is the ballot's
// act, and `state.ts` owns which acts a state permits) and is simply not rendered by
// this panel.
//
// ── PREPARING IS INSTANT; THE TWO TERMINAL ACTS ARE CONFIRMED ──────────────
// Marking a draft ready is rebuildable working state (a ready ballot can still be
// abandoned or, once its build is corrected, rebuilt), so it takes no modal.
// ABANDON is terminal and its control is DISCRETE — a ghost secondary, never a
// primary button — opening a Cancel-first confirm that requires a reason. OPEN is
// the other point of no return and is handled by its own step (the parent swaps to
// {@link OpenStep}); the primary button here only navigates to it.
//
// ── THE VOTING WINDOW HANGS OFF EACH ROW, DISCREETLY ───────────────────────
// A ballot carries one motion or one election, so its window IS that item's voting
// window — which is why {@link SchedulePanel} is per ROW and not a panel-level
// control. It is offered only while the ballot is `draft` or `ready` (an open ballot's
// window is fixed, and the worker answers `409 schedule_not_editable`), and it sits
// BELOW the row's acts behind a closed disclosure: setting a clock is not the panel's
// subject, and by-hand — today's behaviour — stays the default that needs no press.
//
// The whole ROW is what the panel reads its recorded window from: `GET /api/setup/
// :electionId` carries a `schedule` object on every ballot, so the panel is handed
// the `ballot` rather than four unpacked props and states the window on page load. A
// window held only in the panel that set it would vanish on the next refresh.
//
// ── THE IN-APP BUILDER IS THE NEXT PHASE (P3a-2) ───────────────────────────
// This panel takes EXISTING drafts from draft to open. Building a ballot in the app
// — the position/motion form — is the follow-on, so there is a plain seam note
// where its "Build a ballot" control will attach, and NO stub button: a disabled
// "coming soon" primary is furniture that looks like an act. Today a draft is
// created by the seed script; this is stated rather than left as a puzzle about why
// the list might be empty.

import { useState, type JSX } from 'react';
import { ConfirmBlock } from './ConfirmBlock';
import { SchedulePanel } from './SchedulePanel';
import { isAbandonReasonValid, permittedBallotActs, ABANDON_REASON_MAX } from './state';
import type { DraftBallot } from '../api/client';

/** The kind word an operator reads — an election fills positions, a motion decides
 *  a question. The wire word is the `ballot.item_kind` CHECK's. Exported for
 *  {@link OpenPollsPanel}, which names the same kinds: one table, so the two panels
 *  on one screen cannot start calling a `position` different things. */
export const KIND_LABEL: Record<string, string> = {
  position: 'Election',
  motion: 'Motion',
};

/** The three states this surface lists, as a word — the shape (a dot) carries it
 *  again, and colour carries nothing (Miranda: state is a word and a shape, never
 *  colour alone).
 *
 *  **`open` WAS MISSING AND THE ROW PRINTED THE RAW WIRE WORD**, lowercase, the
 *  moment `GET /api/setup/:electionId` widened to list running polls — the fall-through
 *  at the call site is honest but it is a machine's word, not an operator's. Exported
 *  with {@link KIND_LABEL} for the same reason. */
export const STATE_LABEL: Record<string, string> = {
  draft: 'Draft',
  ready: 'Ready',
  open: 'Open',
};

/**
 * Is this state's dot HOLLOW? An ALLOWLIST of the filled words, never a test for the
 * hollow ones, and the direction is the point.
 *
 * ballot.css settles the shape's meaning — "Open = filled, closed = hollow", the shape
 * carrying the state and the word carrying it again. FILLED therefore means a ballot
 * that has been settled or enlivened: `ready` (built, and the Administrator has said
 * so) or `open` (running, binding, people are voting in it right now). Hollow means
 * `draft` — still being built.
 *
 * **THE BUG THIS REPLACES: `ballot.state !== 'ready'`.** That gave a RUNNING BINDING
 * POLL the same hollow dot as an unbuilt draft — the one genuinely misleading render
 * on the row, because the shape is what an operator reads at a glance across a hall.
 * It is written as an allowlist so the failure direction is safe: a state word this
 * build has never heard of reads as NOT-settled rather than being drawn as though it
 * were live.
 */
export function stateDotIsHollow(state: string): boolean {
  return !(state === 'ready' || state === 'open');
}

interface Props {
  ballots: DraftBallot[];
  rollFrozen: boolean;
  busy: boolean;
  /** The ballot id of the act in flight. During any in-flight act ALL rows' buttons
   *  lock — they share this one busy/pending pair, so true per-row liveness would
   *  race two mutations' `finally` blocks. `pending` does NOT keep the other rows
   *  live; it only identifies the acting row, so it alone shows "Working…". */
  pending: string | null;
  onMarkReady: (ballotId: string) => void;
  onAbandon: (ballotId: string, reason: string) => void;
  onOpen: (ballot: DraftBallot) => void;
  /** Open the in-app ballot builder (P3a-2). */
  onBuild: () => void;
}

export function BallotsPanel({
  ballots,
  rollFrozen,
  busy,
  pending,
  onMarkReady,
  onAbandon,
  onOpen,
  onBuild,
}: Props): JSX.Element {
  return (
    <section className="setup-panel card" aria-labelledby="setup-ballots-heading">
      <h2 className="setup-panel-heading" id="setup-ballots-heading">
        The ballots
      </h2>

      {ballots.length === 0 ? (
        <p className="setup-panel-note">No ballots have been drafted for this sitting yet.</p>
      ) : (
        <ul className="setup-ballot-list">
          {ballots.map((ballot) => (
            <DraftRow
              key={ballot.id}
              ballot={ballot}
              rollFrozen={rollFrozen}
              busy={busy}
              isPending={pending === ballot.id}
              onMarkReady={() => onMarkReady(ballot.id)}
              onAbandon={(reason) => onAbandon(ballot.id, reason)}
              onOpen={() => onOpen(ballot)}
            />
          ))}
        </ul>
      )}

      {/* Build a ballot in the app — the election or motion form (P3a-2). A discrete
          ghost, not a primary: building is one act among the panel's several, and the
          draft list above is the panel's subject. */}
      <div className="setup-seam">
        <p className="setup-panel-note">
          Build a new ballot for this sitting — an election to fill a position, or a motion to put a
          question to Synod. It is saved as a draft you can build up and mark ready.
        </p>
        <button
          type="button"
          className="btn btn-ghost setup-build"
          disabled={busy}
          onClick={onBuild}
        >
          Build a ballot
        </button>
      </div>
    </section>
  );
}

interface RowProps {
  ballot: DraftBallot;
  rollFrozen: boolean;
  busy: boolean;
  isPending: boolean;
  onMarkReady: () => void;
  onAbandon: (reason: string) => void;
  onOpen: () => void;
}

function DraftRow({
  ballot,
  rollFrozen,
  busy,
  isPending,
  onMarkReady,
  onAbandon,
  onOpen,
}: RowProps): JSX.Element {
  const [abandoning, setAbandoning] = useState(false);
  const [reason, setReason] = useState('');

  const acts = permittedBallotActs(ballot.state, rollFrozen);
  // The voting window is editable through the SCHEDULE route only while the ballot is
  // still being prepared; once it opens the worker answers `schedule_not_editable`, and
  // every control in that panel would be refused. It is NOT relaxed for an open ballot:
  // moving a running poll's close is a different act with a different fence, and it is
  // reached from the hub's "Open now" panel (`acts.extend`), not from here.
  const scheduleEditable = ballot.state === 'draft' || ballot.state === 'ready';
  const kind = KIND_LABEL[ballot.itemKind] ?? ballot.itemKind;
  const stateWord = STATE_LABEL[ballot.state] ?? ballot.state;
  const hollow = stateDotIsHollow(ballot.state);
  const reasonId = `abandon-reason-${ballot.id}`;
  // During any in-flight act ALL rows' buttons lock (they share one busy/pending
  // pair — the buttons below are `disabled={busy}` — so true per-row liveness would
  // race two mutations' `finally` blocks). `rowBusy` is NOT that lock: it only marks
  // the acting row, so it alone shows "Working…" while the rest sit disabled.
  const rowBusy = busy && isPending;

  return (
    <li className="setup-ballot">
      <div className="setup-ballot-head">
        <h3 className="setup-ballot-title">{ballot.title}</h3>
        <p className="setup-ballot-meta">
          <span className="setup-ballot-kind">{kind}</span>
          <span className="setup-ballot-statelabel">
            <span
              className={`ballot-dot${hollow ? ' ballot-dot-hollow' : ''}`}
              aria-hidden="true"
            />
            {stateWord}
          </span>
        </p>
      </div>

      {ballot.definitionDigest !== null ? (
        <details className="setup-ballot-digest">
          <summary className="result-provenance-summary">
            The definition this was built from
          </summary>
          <p className="result-mono setup-digest-value">{ballot.definitionDigest}</p>
        </details>
      ) : null}

      {abandoning ? (
        <ConfirmBlock
          prompt={`Abandon “${ballot.title}”? This draft will not be used, and it cannot be undone.`}
          confirmLabel="Yes, abandon this ballot"
          busy={rowBusy}
          confirmDisabled={!isAbandonReasonValid(reason)}
          onConfirm={() => onAbandon(reason)}
          onCancel={() => setAbandoning(false)}
        >
          <div className="setup-field">
            <label htmlFor={reasonId}>Why is this ballot being abandoned? (required)</label>
            <textarea
              id={reasonId}
              className="setup-input setup-textarea"
              maxLength={ABANDON_REASON_MAX}
              value={reason}
              disabled={rowBusy}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </ConfirmBlock>
      ) : (
        <>
          <div className="setup-ballot-actions">
            {acts.markReady ? (
              <button
                type="button"
                className="btn btn-primary setup-primary"
                disabled={busy}
                onClick={onMarkReady}
              >
                {rowBusy ? 'Working…' : 'Mark ready'}
              </button>
            ) : null}
            {acts.open ? (
              <button
                type="button"
                className="btn btn-primary setup-primary"
                disabled={busy}
                onClick={onOpen}
              >
                Open the ballot
              </button>
            ) : null}
            {/* Offered when the ballot is ready but the roll is not yet frozen: the
                honest reason the Open button is absent, so the operator is not left
                guessing. */}
            {ballot.state === 'ready' && !rollFrozen ? (
              <span className="setup-field-hint">Freeze the roll to open this ballot.</span>
            ) : null}
            {/* An OPEN poll's row carries no control at all — the same honest-reason
                idiom as the line above it. Its one act is in the "Open now" panel at
                the top of the hub, where the closing time it moves is on the screen
                beside it; a row with no controls and no explanation reads as broken. */}
            {acts.extend ? (
              <span className="setup-field-hint">
                This poll is running. Give it more time from “Open now”, at the top of this page.
              </span>
            ) : null}
            {acts.abandon ? (
              <button
                type="button"
                className="btn btn-ghost setup-abandon"
                disabled={busy}
                onClick={() => setAbandoning(true)}
              >
                Abandon
              </button>
            ) : null}
          </div>

          {/* BELOW the acts, and closed: the window is a secondary setting on a
              ballot, not one of its lifecycle acts. `busy` is passed so a schedule
              cannot be saved into the middle of a freeze or an open. */}
          {scheduleEditable ? <SchedulePanel ballot={ballot} busy={busy} /> : null}
        </>
      )}
    </li>
  );
}
